// Audio shared by the practice dock's tools: one AudioContext for everything
// that makes or hears sound, and one microphone that the tuner, the chord
// listener and the chord-change trainer all listen through. Browsers ask for
// the microphone once per stream, so sharing it means one prompt and one
// "recording" indicator however many tools are listening, and the stream is
// stopped as soon as the last of them lets go.

let context = null;

const AudioContextClass = () => window.AudioContext || window.webkitAudioContext;

export const isAudioSupported = () => !!AudioContextClass();

export function getAudioContext() {
    if (!context) {
        const Context = AudioContextClass();
        context = new Context({ latencyHint: 'interactive' });
    }
    return context;
}

// Browsers only let audio start from a user gesture: call this inside the
// click that starts a tool. It's a no-op when the context is already running.
export function resumeAudio() {
    const ctx = getAudioContext();
    ctx.resume().catch(() => {});
    return ctx;
}

class SharedMicrophone {
    constructor() {
        this.claims = new Map();
        this.stream = null;
        this.source = null;
        this.echoCancellation = null;
        this.opening = null;
        this.listeners = new Set();
    }

    static isSupported() {
        return isAudioSupported() && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    }

    // Everything listening connects to this node, which stays put when the
    // stream underneath is reopened with different processing.
    get input() {
        const ctx = getAudioContext();
        if (!this.inputNode || this.inputNode.context !== ctx) {
            this.inputNode = ctx.createGain();
            // Some browsers only process nodes that reach the destination; a
            // muted path keeps analysers running without playing the mic back.
            this.sink = ctx.createGain();
            this.sink.gain.value = 0;
            this.sink.connect(ctx.destination);
        }
        return this.inputNode;
    }

    // Connect an analyser (or any node) to the microphone.
    connect(node) {
        this.input.connect(node);
        node.connect(this.sink);
    }

    // Doesn't go through `input`, which would open a new AudioContext when
    // a tool lets go after the dock has closed the old one.
    disconnect(node) {
        try {
            if (this.inputNode) this.inputNode.disconnect(node);
            if (this.sink) node.disconnect(this.sink);
        } catch (e) {
            // Already disconnected.
        }
    }

    get active() {
        return this.claims.size > 0;
    }

    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    notify() {
        this.listeners.forEach(listener => listener(this.active));
    }

    // owner: any object, released again with release(owner). echoCancellation
    // is wanted when the dock's own sound comes out of speakers (it keeps the
    // backing track's bass out of the chord listener) and unwanted otherwise,
    // since it smears pitch; the stream has it on when any owner wants it.
    // Resolves with the input node; rejects with the getUserMedia error.
    async acquire(owner, { echoCancellation = false } = {}) {
        this.claims.set(owner, { echoCancellation });
        try {
            await this.update();
        } catch (error) {
            this.claims.delete(owner);
            throw error;
        }
        if (!this.claims.has(owner)) {
            throw new DOMException('Released while opening', 'AbortError');
        }
        this.notify();
        return this.input;
    }

    release(owner) {
        if (!this.claims.delete(owner)) return;
        if (this.claims.size === 0) {
            this.close();
        } else {
            this.update().catch(() => {});
        }
        this.notify();
    }

    async update() {
        // One getUserMedia at a time; whoever waited re-checks afterwards.
        while (this.opening) {
            try {
                await this.opening;
            } catch (e) {
                // The other caller handles its own error.
            }
        }
        if (this.claims.size === 0) return;
        const wanted = Array.from(this.claims.values()).some(claim => claim.echoCancellation);
        if (this.stream && wanted === this.echoCancellation) return;
        this.opening = this.open(wanted);
        try {
            await this.opening;
        } finally {
            this.opening = null;
        }
    }

    async open(echoCancellation) {
        // Noise suppression and auto gain are built for speech and flatten a
        // strummed chord, so they stay off either way.
        const stream = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation, noiseSuppression: false, autoGainControl: false },
        });
        if (this.claims.size === 0) {
            stream.getTracks().forEach(track => track.stop());
            return;
        }
        const source = getAudioContext().createMediaStreamSource(stream);
        source.connect(this.input);
        this.stopStream();
        this.stream = stream;
        this.source = source;
        this.echoCancellation = echoCancellation;
    }

    stopStream() {
        if (this.source) this.source.disconnect();
        if (this.stream) this.stream.getTracks().forEach(track => track.stop());
        this.source = null;
        this.stream = null;
        this.echoCancellation = null;
    }

    close() {
        this.stopStream();
    }
}

export const microphone = new SharedMicrophone();
export const isMicrophoneSupported = () => SharedMicrophone.isSupported();

// Closing the dock (leaving the song page) lets go of everything.
export function closeAudio() {
    microphone.claims.clear();
    microphone.close();
    microphone.notify();
    if (context) {
        context.close().catch(() => {});
        context = null;
    }
}
